/**
 * Validation utilities for SupportDesk
 */

import { validationRules } from '@/config/app';

/**
 * Email validation
 */
export const validateEmail = (email: string): boolean => {
  return validationRules.email.test(email.trim());
};

/**
 * Password validation
 */
export const validatePassword = (password: string): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];
  const rules = validationRules.password;

  if (password.length < rules.minLength) {
    errors.push(`Password must be at least ${rules.minLength} characters long`);
  }

  if (rules.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (rules.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (rules.requireNumbers && !/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (rules.requireSpecialChars && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Username validation
 */
export const validateUsername = (username: string): {
  isValid: boolean;
  error?: string;
} => {
  const rules = validationRules.username;
  const trimmed = username.trim();

  if (trimmed.length < rules.minLength) {
    return {
      isValid: false,
      error: `Username must be at least ${rules.minLength} characters long`,
    };
  }

  if (trimmed.length > rules.maxLength) {
    return {
      isValid: false,
      error: `Username must be no more than ${rules.maxLength} characters long`,
    };
  }

  if (!rules.pattern.test(trimmed)) {
    return {
      isValid: false,
      error: 'Username can only contain letters, numbers, underscores, and hyphens',
    };
  }

  return { isValid: true };
};

/**
 * Phone number validation
 */
export const validatePhone = (phone: string): boolean => {
  const cleaned = phone.replace(/\s/g, '');
  return validationRules.phone.test(cleaned);
};

/**
 * Ticket subject validation
 */
export const validateTicketSubject = (subject: string): {
  isValid: boolean;
  error?: string;
} => {
  const trimmed = subject.trim();
  const rules = validationRules.ticket;

  if (trimmed.length < rules.subjectMinLength) {
    return {
      isValid: false,
      error: `Subject must be at least ${rules.subjectMinLength} characters long`,
    };
  }

  if (trimmed.length > rules.subjectMaxLength) {
    return {
      isValid: false,
      error: `Subject must be no more than ${rules.subjectMaxLength} characters long`,
    };
  }

  return { isValid: true };
};

/**
 * Ticket description validation
 */
export const validateTicketDescription = (description: string): {
  isValid: boolean;
  error?: string;
} => {
  const trimmed = description.trim();
  const rules = validationRules.ticket;

  if (trimmed.length < rules.descriptionMinLength) {
    return {
      isValid: false,
      error: `Description must be at least ${rules.descriptionMinLength} characters long`,
    };
  }

  if (trimmed.length > rules.descriptionMaxLength) {
    return {
      isValid: false,
      error: `Description must be no more than ${rules.descriptionMaxLength} characters long`,
    };
  }

  return { isValid: true };
};

/**
 * File validation
 */
export const validateFile = (file: File): {
  isValid: boolean;
  error?: string;
} => {
  const { appConstants } = require('@/config/app');

  if (file.size > appConstants.maxFileSize) {
    return {
      isValid: false,
      error: `File size must be less than ${Math.round(appConstants.maxFileSize / (1024 * 1024))}MB`,
    };
  }

  if (!appConstants.allowedFileTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'File type is not supported',
    };
  }

  return { isValid: true };
};

/**
 * URL validation
 */
export const validateUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Required field validation
 */
export const validateRequired = (value: any): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return value !== null && value !== undefined;
};

/**
 * Minimum length validation
 */
export const validateMinLength = (value: string, minLength: number): boolean => {
  return value.trim().length >= minLength;
};

/**
 * Maximum length validation
 */
export const validateMaxLength = (value: string, maxLength: number): boolean => {
  return value.trim().length <= maxLength;
};

/**
 * Number validation
 */
export const validateNumber = (value: string): boolean => {
  return !isNaN(Number(value)) && isFinite(Number(value));
};

/**
 * Integer validation
 */
export const validateInteger = (value: string): boolean => {
  return Number.isInteger(Number(value));
};

/**
 * Positive number validation
 */
export const validatePositiveNumber = (value: string): boolean => {
  const num = Number(value);
  return validateNumber(value) && num > 0;
};

/**
 * Date validation
 */
export const validateDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Future date validation
 */
export const validateFutureDate = (dateString: string): boolean => {
  if (!validateDate(dateString)) return false;
  const date = new Date(dateString);
  return date > new Date();
};

/**
 * Past date validation
 */
export const validatePastDate = (dateString: string): boolean => {
  if (!validateDate(dateString)) return false;
  const date = new Date(dateString);
  return date < new Date();
};

/**
 * Generic form validation function
 */
export const validateForm = <T extends Record<string, any>>(
  data: T,
  rules: Partial<Record<keyof T, ((value: any) => boolean | { isValid: boolean; error?: string })[]>>
): {
  isValid: boolean;
  errors: Partial<Record<keyof T, string>>;
} => {
  const errors: Partial<Record<keyof T, string>> = {};

  for (const [field, validators] of Object.entries(rules) as [keyof T, any][]) {
    const value = data[field];
    
    for (const validator of validators || []) {
      const result = validator(value);
      
      if (typeof result === 'boolean') {
        if (!result) {
          errors[field] = `${String(field)} is invalid`;
          break;
        }
      } else {
        if (!result.isValid) {
          errors[field] = result.error || `${String(field)} is invalid`;
          break;
        }
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};